var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import { Schema, type, required } from "../decorators/type.js";
import "ajv";
import "./clone.js";
import "bitecs";
import "./typehelpers.js";
import "../enums-86f58524.js";
import "../global-33d5eebf.js";
var __defProp2 = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp2(target, key, result);
  return result;
};
class Vector2dSchema extends Schema {
  constructor() {
    super(...arguments);
    __publicField(this, "x");
    __publicField(this, "y");
  }
}
__decorateClass([
  type("number"),
  required()
], Vector2dSchema.prototype, "x", 2);
__decorateClass([
  type("number"),
  required()
], Vector2dSchema.prototype, "y", 2);
const isVector2d = (v) => {
  if (typeof v !== "object") {
    return false;
  }
  return "x" in v && "y" in v;
};
const w = (v) => ({ x: v.x, y: v.y });
const BV2 = {
  lerpVector2d: (x1, y1, x2, y2, t) => {
    return [x1 + (x2 - x1) * t, y1 + (y2 - y1) * t];
  },
  normalizeVector2d: (x, y) => {
    const length = Math.sqrt(x * x + y * y);
    return [x / length, y / length];
  },
  rotateDegVector2d: (x, y, degrees) => {
    if (degrees % 360 === 0) {
      return [x, y];
    }
    const rad = degrees * Math.PI / 180;
    return BV2.rotateVector2d(x, y, rad);
  },
  rotateVector2d(x, y, radians) {
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    return [x * cos - y * sin, x * sin + y * cos];
  },
  pointTowardsVector2d(x1, y1, x2, y2) {
    if (x1 === x2 && y1 === y2) {
      return [0, 1];
    }
    return BV2.normalizeVector2d(x2 - x1, y2 - y1);
  },
  normalizeOrRandomizeVector2d(x, y, rand) {
    if (x === 0 && y === 0) {
      const random = rand.int(0, 100) / 100;
      return BV2.normalizeVector2d(random - 0.5, 1 - random - 0.5);
    }
    return BV2.normalizeVector2d(x, y);
  },
  distanceSquaredVector2d(x1, y1, x2, y2) {
    return (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1);
  },
  angleBetweenVector2d(x1, y1, x2, y2) {
    const angle = Math.atan2(y2 - y1, x2 - x1);
    return angle * 180 / Math.PI;
  },
  multiply(x1, y1, x2, y2) {
    return [x1 * x2, y1 * y2];
  },
  scaleVector2d(x, y, scale) {
    return [x * scale, y * scale];
  }
};
const perpendicularVector2d = (v) => {
  return { x: -v.y, y: v.x };
};
const equalVector2d = (a, b) => a.x === b.x && a.y === b.y;
const truncVector2d = (a, trunc = 1e4) => {
  return {
    x: Math.floor(a.x * trunc) / trunc,
    y: Math.floor(a.y * trunc) / trunc
  };
};
const clamp = (a, trunc = 1e4) => {
  a.x = Math.floor(a.x * trunc) / trunc;
  a.y = Math.floor(a.y * trunc) / trunc;
};
const trunc2d = (x, y, trunc = 1e4) => {
  return {
    x: Math.floor(x * trunc) / trunc,
    y: Math.floor(y * trunc) / trunc
  };
};
const lerpScalar = (a, b, t) => a + (b - a) * t;
const lerpVector2d = (a, b, t) => {
  return {
    x: lerpScalar(a.x, b.x, t),
    y: lerpScalar(a.y, b.y, t)
  };
};
const distanceSquaredVector2d = (a, b) => {
  return (a.x - b.x) * (a.x - b.x) + (a.y - b.y) * (a.y - b.y);
};
const distanceVector2d = (a, b) => {
  return Math.sqrt(distanceSquaredVector2d(a, b));
};
const inDistanceVector2d = (origin, search, distance) => {
  return distanceSquaredVector2d(origin, search) <= distance * distance;
};
const inDistanceFastVector2d = (ox, oy, sx, sy, distance) => {
  return (sx - ox) * (sx - ox) + (sy - oy) * (sy - oy) <= distance * distance;
};
const normalizeOrRandomizeVector2d = (vector, rand) => {
  if (vector.x === 0 && vector.y === 0) {
    const random = rand.int(0, 100) / 100;
    return normalizeVector2d(w({ x: random - 0.5, y: 1 - random - 0.5 }));
  }
  return normalizeVector2d(vector);
};
const lengthVector2d = (vector) => {
  return distanceVector2d(w({ x: 0, y: 0 }), vector);
};
const rotationDegVector2d = (vector) => {
  return rotationVector2d(vector) * 180 / Math.PI;
};
const rotationVector2d = (vector) => {
  return Math.atan2(vector.y, vector.x);
};
const normalizeSafeVector2d = (vector) => {
  if (vector.x === 0 && vector.y === 0) {
    return w({ x: 0, y: 0 });
  }
  return normalizeVector2d(vector);
};
const normalizeVector2d = (a) => {
  const length = lengthVector2d(a);
  return w(trunc2d(a.x / length, a.y / length));
};
const subtractVector2d = (a, b) => {
  return w({
    x: a.x - b.x,
    y: a.y - b.y
  });
};
const addVector2d = (a, b, c) => {
  return w({
    x: a.x + b.x + ((c == null ? void 0 : c.x) ?? 0),
    y: a.y + b.y + ((c == null ? void 0 : c.y) ?? 0)
  });
};
const scaleVector2d = (a, scale) => {
  return w({
    x: a.x * scale,
    y: a.y * scale
  });
};
const angleOfVector2d = (a) => {
  return angleBetweenVector2d(w({ x: 1, y: 0 }), a);
};
const angleBetweenVector2d = (a, b) => {
  let angle = Math.atan2(b.y, b.x) - Math.atan2(a.y, a.x);
  if (angle < 0) {
    angle += 2 * Math.PI;
  }
  return angle * 180 / Math.PI;
};
const degreesFromVector2d = (a) => {
  return Math.atan2(a.y, a.x) * 180 / Math.PI;
};
const rotateDegVector2d = (a, degrees) => {
  if (degrees % 360 === 0) {
    return w({
      ...a
    });
  }
  const rad = degrees * Math.PI / 180;
  return rotateVector2d(a, rad);
};
const multiplyVector2d = (a, b) => w({
  x: a.x * b.x,
  y: a.y * b.y
});
const dotVector2d = (a, b) => {
  return a.x * b.x + a.y * b.y;
};
const rotateVector2d = (a, radians) => {
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return w(trunc2d(a.x * cos - a.y * sin, a.x * sin + a.y * cos));
};
const pointingTowardsVector2d = (a, b) => {
  return dotVector2d(a, b) > 0;
};
const pointTowardsVector2d = (origin, target) => {
  let dir = w({ x: 0, y: 0 });
  if (!Array.isArray(target)) {
    dir = subtractVector2d(target, origin);
  } else {
    for (const t of target) {
      dir = addVector2d(dir, subtractVector2d(t, origin));
    }
  }
  dir = normalizeVector2d(dir);
  if (isNaN(dir.x)) {
    dir = w({ x: 0, y: 0 });
  }
  return dir;
};
const seekTowardsVector2d = (origin, speed = 1, target) => {
  if (equalVector2d(target, origin)) {
    return origin;
  }
  const pointVector = pointTowardsVector2d(origin, target);
  return addVector2d(origin, speed > 1 ? scaleVector2d(pointVector, speed) : pointVector);
};
const avoidVector2d = (origin, speed = 1, target) => {
  if (Array.isArray(target) && target.length === 0) {
    return origin;
  }
  const pointVector = pointTowardsVector2d(origin, target);
  return addVector2d(origin, scaleVector2d(pointVector, -speed));
};
const isCloserVector2d = (origin, a, b) => {
  const distanceA = distanceVector2d(origin, a);
  const distanceB = distanceVector2d(origin, b);
  return distanceA < distanceB;
};
export {
  BV2,
  Vector2dSchema,
  addVector2d,
  angleBetweenVector2d,
  angleOfVector2d,
  avoidVector2d,
  clamp,
  degreesFromVector2d,
  distanceSquaredVector2d,
  distanceVector2d,
  dotVector2d,
  equalVector2d,
  inDistanceFastVector2d,
  inDistanceVector2d,
  isCloserVector2d,
  isVector2d,
  lengthVector2d,
  lerpScalar,
  lerpVector2d,
  multiplyVector2d,
  normalizeOrRandomizeVector2d,
  normalizeSafeVector2d,
  normalizeVector2d,
  perpendicularVector2d,
  pointTowardsVector2d,
  pointingTowardsVector2d,
  rotateDegVector2d,
  rotateVector2d,
  rotationDegVector2d,
  rotationVector2d,
  scaleVector2d,
  seekTowardsVector2d,
  subtractVector2d,
  trunc2d,
  truncVector2d
};
//# sourceMappingURL=vector.js.map
